import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, List, Modal, Space, Spin, Typography, message } from 'antd';
import { FolderOpenOutlined } from '@ant-design/icons';

import { useAuth } from '../common/security/AuthContext';
import { dccListStats, type DccListStatsItem } from '../../services/dccApi';

const { Text } = Typography;

// Props compatible with the dynamic modalRegistry (open, value, onOk, onCancel, options)
// plus explicit basePath/onSelect for direct usage in feature UIs.
export interface RootFolderSelectProps {
  open: boolean;
  value?: string | null;
  onOk?: (value: string) => void;
  onCancel: () => void;
  options?: any[];

  /**
   * Base DCC path to list under, e.g. '' or 'uploads'.
   * Only first-level subfolders will be listed.
   */
  basePath?: string;

  /** Optional direct callback used by feature pages like DccIndexTables. */
  onSelect?: (folderPath: string) => void;
}

const normalizeBasePath = (path: string | undefined): string => {
  const p = String(path || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
  return p;
};

const RootFolderSelect: React.FC<RootFolderSelectProps> = ({
  open,
  value,
  onOk,
  onCancel,
  basePath = '',
  onSelect,
}) => {
  const { token } = useAuth();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<DccListStatsItem[]>([]);
  const [selectedPath, setSelectedPath] = useState<string | null>(value ?? null);

  const [currentBase, setCurrentBase] = useState<string>(normalizeBasePath(basePath));
  const [nameFilter, setNameFilter] = useState<string>('');

  const loadFolders = async (baseOverride?: string) => {
    const effectiveBase = normalizeBasePath(baseOverride ?? currentBase);
    if (!token) {
      message.error('Session not found. Please log in again.');
      return;
    }
    setLoading(true);
    try {
      const stats = await dccListStats(effectiveBase, token);
      const onlyFolders = (stats || []).filter((item) => item.is_dir);
      setItems(onlyFolders);
      setSelectedPath(value ?? null);
      setCurrentBase(effectiveBase);
    } catch (e: any) {
      message.error(e?.message || 'Failed to load DCC folders.');
      setItems([]);
      setSelectedPath(value ?? null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const base = normalizeBasePath(basePath);
    setCurrentBase(base);
    setSelectedPath(null);
    setNameFilter('');
    loadFolders(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, basePath]);

  // Keep internal selection in sync with external value when reopened
  useEffect(() => {
    if (!open) return;
    setSelectedPath(value ?? null);
  }, [value, open]);

  const handleConfirm = () => {
    if (!selectedPath) return;
    if (onSelect) {
      onSelect(selectedPath);
    }
    if (onOk) {
      onOk(selectedPath);
    }
    onCancel();
  };

  const renderPath = (item: DccListStatsItem) => {
    const base = currentBase;
    if (!base) return item.path;
    return item.path.startsWith(base + '/') ? item.path.slice(base.length + 1) : item.path;
  };

  const filteredItems = useMemo(() => {
    const raw = (nameFilter || '').trim().toLowerCase();
    if (!raw) return items;
    const terms = raw.split(/[\s]+/).filter(Boolean);
    if (terms.length === 0) return items;

    return items.filter((item) => {
      const label = (renderPath(item) || item.path || '').toLowerCase();
      return terms.every((t) => label.includes(t));
    });
  }, [items, nameFilter, currentBase]);

  return (
    <Modal
      title="Select DCC Folder"
      open={open}
      onCancel={onCancel}
      width={560}
      footer={null}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <Input
            key={currentBase || 'root'}
            allowClear
            placeholder="Filter folders (multiple words = AND search)"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            style={{ flex: 1 }}
          />
          <Space>
            <Button
              size="small"
              onClick={() => {
                const rootBase = normalizeBasePath(basePath);
                setSelectedPath(null);
                loadFolders(rootBase);
              }}
              disabled={loading}
            >
              Root
            </Button>
            <Button
              size="small"
              onClick={() => {
                const parts = (currentBase || '').split('/').filter(Boolean);
                if (parts.length === 0) {
                  const rootBase = normalizeBasePath(basePath);
                  setSelectedPath(null);
                  loadFolders(rootBase);
                  return;
                }
                parts.pop();
                const parent = parts.join('/');
                setSelectedPath(null);
                loadFolders(parent);
              }}
              disabled={loading && !currentBase}
            >
              Up
            </Button>
          </Space>
        </div>
        <Spin spinning={loading}>
          <List
            bordered
            size="small"
            dataSource={filteredItems}
            locale={{ emptyText: 'No folders found.' }}
            renderItem={(item) => {
              const relative = renderPath(item);
              const fullPath = item.path;
              const isSelected = selectedPath === fullPath;
              return (
                <List.Item
                  key={fullPath}
                  style={{
                    cursor: 'pointer',
                    background: isSelected ? '#e6f7ff' : undefined,
                  }}
                  onClick={() => setSelectedPath(fullPath)}
                  onDoubleClick={() => {
                    setSelectedPath(fullPath);
                    if (onSelect) {
                      onSelect(fullPath);
                    }
                    if (onOk) {
                      onOk(fullPath);
                    }
                    onCancel();
                  }}
                  actions={[
                    <Button
                      key="open"
                      size="small"
                      type="link"
                      icon={<FolderOpenOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPath(null);
                        loadFolders(fullPath);
                      }}
                    >
                      Open
                    </Button>,
                  ]}
                >
                  <Space>
                    <FolderOpenOutlined />
                    <Text>{relative || fullPath}</Text>
                  </Space>
                </List.Item>
              );
            }}
          />
        </Spin>
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" onClick={handleConfirm} disabled={!selectedPath}>
            Select
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};

export default RootFolderSelect;
